// frontend/src/presentation/hooks/usePttManager.ts
//
// Menedżer PTT (Push-To-Talk) — spina CZTERY niezależne źródła sygnału w jeden
// spójny strumień „wciśnięto / puszczono":
//
//   1. WebHID   — uniwersalna gruszka USB (`WebHidPttService`), P0 ze specyfikacji.
//   2. Media Session API — słuchawka Bluetooth (`useBluetoothButton`).
//   3. Symulacja klawiaturowa — F1 / Spacja, do testów biurkowych (`usePttSimulation`).
//   4. Natywny mostek Android — lokalny serwer HTTP 127.0.0.1:8842 odpytywany w
//      pętli (`NativeBridgePttSource`); jedyny kanał działający przy zgaszonym
//      ekranie (patrz `native-bridge/Integracja_Mostka_PTT.pdf`).
//
// Każde źródło ma WŁASNĄ logikę wykrywania wciśnięcia/puszczenia (Media Session
// zna tylko 'play'/'pause'/toggle, klawiatura zna `keydown`/`keyup`, WebHID zna
// surowe raporty), ale konsument (np. `Screen1`) chce JEDNEJ pary callbacków
// `onPress`/`onRelease` — dokładnie tak, jak dotąd wołał `handleBluetoothStart`/
// `handleBluetoothStop` z dwóch osobnych hooków.
//
// Scalanie: źródła trzymane są jako zbiór aktywnych identyfikatorów.
// `onPress` woła się TYLKO przy przejściu zbioru z pustego na niepusty,
// `onRelease` TYLKO przy powrocie do pustego — dzięki temu przytrzymanie
// jednocześnie np. gruszki USB i klawisza symulacji (biurko dewelopera z
// podłączonym sprzętem) nie generuje podwójnego startu dyktowania, a puszczenie
// jednego z nich nie ucina nagrania, dopóki drugie źródło jest wciąż wciśnięte.

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  WebHidPttService,
  type HIDDeviceFilter,
  type WebHidRequestOutcome,
} from '../../infrastructure/hid/WebHidPttService'
import { NativeBridgePttSource } from '../../infrastructure/nativeBridge/NativeBridgePttSource'
import { useBluetoothButton } from './useBluetoothButton'
import {
  usePttSimulation,
  type PttSimulationController,
  type UsePttSimulationOptions,
} from '../../hooks/usePttSimulation'

/** Źródła sygnału PTT scalane w jeden strumień. */
type PttSource = 'webhid' | 'mediaSession' | 'keyboardSim' | 'nativeBridge'

/** Konfiguracja warstwy symulacji klawiaturowej — przekazywana 1:1 do `usePttSimulation`. */
export type PttManagerSimulationOptions = Omit<UsePttSimulationOptions, 'onPress' | 'onRelease'>

export interface UsePttManagerOptions {
  /** Wołane, gdy KTÓREKOLWIEK źródło zainicjuje wciśnięcie (pierwsze z aktywnych). */
  onPress: () => void
  /** Wołane, gdy WSZYSTKIE aktywne źródła puszczą przycisk. */
  onRelease: () => void
  /** Opcjonalny filtr urządzeń dla natywnego pickera WebHID (domyślnie: pokaż wszystkie). */
  webHidFilters?: readonly HIDDeviceFilter[]
  /** Heurystyka wciśnięcia dla niestandardowej gruszki (domyślnie: dowolny niezerowy bajt raportu). */
  webHidIsPressedReport?: (data: DataView) => boolean
  /** Nadpisania symulacji klawiaturowej (np. `enabled`, `keys`) — reszta jak w `usePttSimulation`. */
  simulation?: PttManagerSimulationOptions
  /** Nadpisania warstwy natywnego mostka PTT (Android Foreground Service → HTTP polling 127.0.0.1:8842). */
  nativeBridge?: {
    /** Adres bazowy lokalnego serwera mostka. Domyślnie `http://127.0.0.1:8842`. */
    baseUrl?: string
    /** Odstęp odpytywania mostka w ms. Domyślnie 700. */
    pollIntervalMs?: number
    /**
     * Czy w ogóle uruchamiać pętlę odpytywania mostka. Domyślnie `true` TYLKO na
     * Androidzie — serwer mostka (`127.0.0.1:8842`) istnieje wyłącznie w natywnej
     * appce, więc na desktopie / w zwykłej przeglądarce poller co 700 ms tylko
     * zalewałby konsolę `ERR_CONNECTION_REFUSED`. Jawne `true`/`false` nadpisuje
     * detekcję (m.in. w testach).
     */
    enabled?: boolean
  }
}

export interface PttManagerController {
  /** Czy którekolwiek źródło jest aktualnie aktywne (scalony stan „wciśnięty"). */
  isPressed: boolean
  /** Czy przeglądarka w ogóle wspiera WebHID (Chrome/Edge) — do ukrycia przycisku parowania na Safari/Firefox. */
  isWebHidSupported: boolean
  /** Czy jakakolwiek gruszka USB jest aktualnie podłączona i nasłuchiwana. */
  isWebHidConnected: boolean
  /** Nazwy podłączonych urządzeń USB (do etykiety w UI). */
  webHidDeviceNames: readonly string[]
  /**
   * Otwiera natywny picker WebHID (`navigator.hid.requestDevice`) — wołać
   * WYŁĄCZNIE z bezpośredniego handlera kliknięcia (wymóg gestu użytkownika).
   */
  requestUsbDevice: () => Promise<WebHidRequestOutcome>
  /** Stan warstwy symulacji klawiaturowej (np. na potrzeby nakładki debug). */
  simulation: PttSimulationController
  /**
   * FIX DESYNCHRONIZACJI hardware ↔ software: zeruje lustro stanu „wciśnięty" we
   * WSZYSTKICH źródłach, które to wspierają (natywny mostek), oraz w scalonym
   * strumieniu menedżera — BEZ emitowania `onRelease`. Wołać po auto-stopie z
   * ciszy (watchdog VAD), żeby kolejne kliknięcie sprzętowego przełącznika znów
   * znaczyło „start", a nie „stop".
   */
  resetAllSources: () => void
}

/**
 * Scala WebHID + Media Session + symulację klawiaturową w jeden strumień PTT.
 *
 * @example
 * ```ts
 * const ptt = usePttManager({ onPress: handleBluetoothStart, onRelease: handleBluetoothStop })
 * // <WebHidPairButton isSupported={ptt.isWebHidSupported} isConnected={ptt.isWebHidConnected} onRequestDevice={ptt.requestUsbDevice} />
 * ```
 */
export function usePttManager({
  onPress,
  onRelease,
  webHidFilters,
  webHidIsPressedReport,
  simulation,
  nativeBridge,
}: UsePttManagerOptions): PttManagerController {
  // Świeże referencje callbacków — źródła montują nasłuch raz, re-render
  // rodzica (Screen1 renderuje się bardzo często) nie może nic odpinać.
  const onPressRef = useRef(onPress)
  const onReleaseRef = useRef(onRelease)
  onPressRef.current = onPress
  onReleaseRef.current = onRelease

  const activeSourcesRef = useRef<Set<PttSource>>(new Set())
  const [isPressed, setIsPressed] = useState(false)

  // Uwaga: KAŻDY z trzech leaf-hooków (`useBluetoothButton`, `usePttSimulation`,
  // `WebHidPttService`) sam pilnuje własnej idempotencji i woła `activateSource`/
  // `deactivateSource` WYŁĄCZNIE na faktycznej zmianie stanu tego źródła — więc
  // `Set.add`/`Set.delete` (naturalnie idempotentne) i `wasEmpty`/`size === 0`
  // liczone względem stanu SPRZED operacji wystarczają, bez dodatkowej strażniczej
  // gałęzi „source już (nie)obecne", której przy takim wywołujących i tak nigdy
  // by nie trafiono.
  const activateSource = useCallback((source: PttSource): void => {
    const sources = activeSourcesRef.current
    const wasEmpty = sources.size === 0
    sources.add(source)
    if (wasEmpty) {
      setIsPressed(true)
      onPressRef.current()
    }
  }, [])

  const deactivateSource = useCallback((source: PttSource): void => {
    const sources = activeSourcesRef.current
    sources.delete(source)
    if (sources.size === 0) {
      setIsPressed(false)
      onReleaseRef.current()
    }
  }, [])

  // ── Źródło 1: Media Session (słuchawka Bluetooth) — bez zmian względem Screen1. ──
  const handleMediaStart = useCallback(() => activateSource('mediaSession'), [activateSource])
  const handleMediaStop = useCallback(() => deactivateSource('mediaSession'), [deactivateSource])
  useBluetoothButton({ onStartRecording: handleMediaStart, onStopRecording: handleMediaStop })

  // ── Źródło 2: symulacja klawiaturowa (F1/Spacja) — bez zmian względem Screen1. ──
  const handleSimPress = useCallback(() => activateSource('keyboardSim'), [activateSource])
  const handleSimRelease = useCallback(() => deactivateSource('keyboardSim'), [deactivateSource])
  const pttSimulation = usePttSimulation({
    ...simulation,
    onPress: handleSimPress,
    onRelease: handleSimRelease,
  })

  // ── Źródło 3: WebHID (gruszka USB) — nowość tego modułu. ──────────────────
  const [isWebHidSupported] = useState(() => WebHidPttService.isSupported())
  const [isWebHidConnected, setIsWebHidConnected] = useState(false)
  const [webHidDeviceNames, setWebHidDeviceNames] = useState<readonly string[]>([])

  // Serwis żyje raz na cały czas życia hooka — `useState` z leniwym
  // inicjalizatorem tworzy go DOKŁADNIE RAZ (nawet pod podwójnym wywołaniem
  // renderu w React Strict Mode konstruktor nie ma efektów ubocznych), a
  // referencja jest stabilna między renderami. `dispose()` sprząta w efekcie
  // przy odmontowaniu.
  const [webHidService] = useState<WebHidPttService>(
    () =>
      new WebHidPttService({
        onPress: () => activateSource('webhid'),
        onRelease: () => deactivateSource('webhid'),
        filters: webHidFilters,
        isPressedReport: webHidIsPressedReport,
      }),
  )

  const syncWebHidState = useCallback((): void => {
    setIsWebHidConnected(webHidService.isConnected)
    setWebHidDeviceNames(webHidService.connectedDeviceNames)
  }, [webHidService])

  // ── Źródło 4: natywny mostek PTT (Android Foreground Service, TRYB TOGGLE) ──
  // Natywna appka wystawia lokalny serwer HTTP (`GET /events?since=<seq>` na
  // 127.0.0.1:8842); klasa odpytuje go w pętli, utrzymuje lustro stanu
  // przełącznika i mapuje kolejne kliknięcia na `activateSource`/`deactivateSource`.
  // Działa też przy zgaszonym ekranie — tam WebHID/Media Session milkną (patrz
  // `native-bridge/Integracja_Mostka_PTT.pdf`). Wyłącznie ono ma `forceReset()`
  // — patrz `resetAllSources` niżej.
  const [nativeBridgeService] = useState<NativeBridgePttSource>(
    () =>
      new NativeBridgePttSource({
        onPress: () => activateSource('nativeBridge'),
        onRelease: () => deactivateSource('nativeBridge'),
        baseUrl: nativeBridge?.baseUrl,
        pollIntervalMs: nativeBridge?.pollIntervalMs,
      }),
  )

  // Poller mostka odpalamy tylko tam, gdzie serwer `127.0.0.1:8842` ma szansę
  // istnieć (natywna appka Android). Domyślnie: detekcja po `userAgent`; jawne
  // `nativeBridge.enabled` nadpisuje (m.in. testy). Wyliczone RAZ — stabilna
  // tożsamość do listy zależności efektu niżej.
  const [nativeBridgeEnabled] = useState<boolean>(
    () =>
      nativeBridge?.enabled ??
      (typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)),
  )

  useEffect(() => {
    // Auto-reconnect: gruszka sparowana w poprzedniej sesji ma działać od
    // razu, bez ponownego klikania „Podłącz" po każdym odświeżeniu strony.
    void webHidService.reconnectGrantedDevices().then(syncWebHidState)
    // Natywny mostek: rusza pętlę odpytywania serwera HTTP. Pierwsze zapytanie
    // ustala tylko punkt startowy (`latestSeq`), nie odtwarza historii — patrz
    // `NativeBridgePttSource.start()`.
    //
    // Serwer mostka (127.0.0.1:8842) istnieje WYŁĄCZNIE wewnątrz natywnej appki
    // Android (`native-bridge/android/`). Na desktopie / w zwykłej przeglądarce
    // port jest głuchy, więc poller co 700 ms tylko zalewałby konsolę
    // `ERR_CONNECTION_REFUSED` — patrz `nativeBridgeEnabled` wyżej.
    if (nativeBridgeEnabled) nativeBridgeService.start()
    return () => {
      webHidService.dispose()
      nativeBridgeService.stop()
    }
  }, [webHidService, nativeBridgeService, syncWebHidState, nativeBridgeEnabled])

  const requestUsbDevice = useCallback(async (): Promise<WebHidRequestOutcome> => {
    const outcome = await webHidService.requestDevice()
    syncWebHidState()
    return outcome
  }, [webHidService, syncWebHidState])

  const resetAllSources = useCallback((): void => {
    // Jedyne źródło z lustrem stanu do zresynchronizowania to natywny mostek
    // (tryb Toggle) — WebHID / Media Session / klawiatura są bezstanowe
    // (edge-driven: każde zdarzenie samo w sobie niesie kierunek).
    nativeBridgeService.forceReset()
    // Scalony strumień też wraca do spoczynku — BEZ `onRelease` (to
    // resynchronizacja luster stanu, nie gest użytkownika).
    activeSourcesRef.current.clear()
    setIsPressed(false)
  }, [nativeBridgeService])

  return {
    isPressed,
    isWebHidSupported,
    isWebHidConnected,
    webHidDeviceNames,
    requestUsbDevice,
    simulation: pttSimulation,
    resetAllSources,
  }
}
